import type { ComponentProps, ReactNode } from 'react'

import { cx } from '@/lib/cx'

const widths = { sm: 'max-w-md', md: 'max-w-2xl', lg: 'max-w-5xl', full: '' }

export interface PageProps extends Omit<ComponentProps<'main'>, 'title'> {
  title?: ReactNode
  /** Content width on large screens; always full width on phones. */
  width?: keyof typeof widths
}

/** Page body: phone padding with safe-area insets, centered and capped on larger screens. */
export function Page({ title, width = 'md', className, children, ...rest }: PageProps) {
  return (
    <main
      className={cx(
        'mx-auto w-full px-4 py-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:px-6 print:p-0',
        widths[width],
        className,
      )}
      {...rest}
    >
      {title && <h1 className="mb-4 text-xl font-semibold">{title}</h1>}
      {children}
    </main>
  )
}

export const CARD = 'bg-surface border-border rounded-xl border p-4'

export function Card({ className, ...rest }: ComponentProps<'section'>) {
  return <section className={cx(CARD, className)} {...rest} />
}

const gaps = { xs: 'gap-1', sm: 'gap-2', md: 'gap-4', lg: 'gap-6' }

export interface StackProps extends ComponentProps<'div'> {
  gap?: keyof typeof gaps
  /** Stacks vertically on phones; `row` turns into a row from `sm` up. */
  row?: boolean
}

export function Stack({ gap = 'md', row = false, className, ...rest }: StackProps) {
  return <div className={cx('flex flex-col', row && 'sm:flex-row sm:items-center', gaps[gap], className)} {...rest} />
}
